/**
 * Singolo livello (Layer) della mappa. Permette di specificare la fonte
 * dei dati, lo stile e le eventuali interazioni.
 */
export interface IMapLayer {
  type: MapLayerType;
  /**
   * Codice stabile e univoco del layer. Identifica il layer nelle funzioni di
   * calcolo (intersezione, competenza) in modo indipendente dal nome
   * visualizzato.
   */
  code: string;
  /**
   * Nome del layer
   */
  name: string;
  /**
   * URL della fonte dei dati
   */
  url: string;
  /**
   * Stile del layer
   */
  style?: IMapLayerStyle;
  /**
   * Zoom minimo per visualizzare il layer
   */
  minZoom?: number;
  /**
   * Proprietà da utilizzare per l'etichettatura delle geometrie
   */
  labelPropertyName?: string | null;

  /** Riferimento al gruppo tematico (name di IMapGroup). Se assente, il layer è nella sezione "Altri". */
  group?: string | null;

  /** Configurazione intersezione per questo layer */
  intersection?: IMapLayerIntersection;

  /** Configurazione interrogazione (Identify) per questo layer */
  identify?: IMapLayerIdentify;

  /** Configurazione competenza per questo layer */
  competence?: IMapLayerCompetence;

  /** Opzioni specifiche del protocollo WFS. Richieste se type === WFS. */
  wfs?: IMapLayerWfsOptions;
}

/**
 * Sottoinsieme della configurazione del layer che identifica la fonte
 * dati: tipo di servizio, URL e opzioni protocollo-specifiche. È la
 * porzione di config condivisa da creazione sorgenti, introspezione e
 * verifica del tipo di servizio: un nuovo protocollo aggiunge qui il
 * suo campo opzioni e viene propagato ovunque.
 */
export type IMapLayerSource = Partial<Pick<IMapLayer, 'type' | 'url' | 'wfs'>>;

/**
 * Versioni WFS supportate. Differiscono per nome dei parametri KVP
 * (`typeName` vs `typeNames`) e versione GML della risposta
 * (3.1.1 vs 3.2.1).
 */
export type WfsVersion = '1.1.0' | '2.0.0';

/**
 * Opzioni di un layer WFS, rilevate a config-time tramite GetCapabilities
 * e persistite nella configurazione: a runtime non servono chiamate extra.
 */
export interface IMapLayerWfsOptions {
  /**
   * Feature type qualificato esposto dal servizio (es. 'osm:water_areas')
   */
  typeName: string;
  /**
   * Versione WFS negoziata con il servizio (default runtime: 2.0.0)
   */
  version?: WfsVersion;
  /**
   * outputFormat per GetFeature dichiarato dal servizio (es.
   * 'application/json'). Assente o null → risposta GML.
   */
  outputFormat?: string | null;
  /**
   * CRS nativo dichiarato dal servizio per il feature type
   * (es. 'EPSG:25832')
   */
  nativeCrs?: string | null;
}

/**
 * Per i layer vettoriali, il valore di default per il livello minimo è 15.
 */
export const DefaultMapLayerMinZoom = 15;

export interface IMapLayerStyle {
  /**
   * Colore di riempimento
   */
  fillColor?: string;
  /**
   * Colore del bordo
   */
  strokeColor?: string;
  /**
   * Spessore del bordo
   */
  strokeWidth?: number;
  /**
   * Opacità
   */
  opacity?: number;
  /**
   * Simbolo con cui vengono rese le feature puntiformi del layer.
   * Assente → il runtime applica il simbolo di default (cerchio).
   */
  symbol?: IMapLayerSymbol | null;
}

/**
 * Simbolo per le feature puntiformi di un layer vettoriale. Eredita i
 * colori (fill/stroke) e lo spessore del bordo dallo stile del layer.
 */
export interface IMapLayerSymbol {
  shape: MapSymbolShape;
  /**
   * Raggio del simbolo in pixel
   */
  size?: number | null;
}

/**
 * Forme supportate per il simbolo delle feature puntiformi. Tutte
 * realizzate a runtime con primitive OpenLayers (Circle/RegularShape);
 * `cross` e `x` sono linee e renderizzano col solo colore del bordo.
 */
export enum MapSymbolShape {
  Circle = 'circle',
  Square = 'square',
  Triangle = 'triangle',
  Star = 'star',
  Cross = 'cross',
  X = 'x',
}

/** Raggio di default del simbolo, in pixel. */
export const DefaultMapSymbolSize = 6;

export const MapSymbolShapeOptions = [
  { value: MapSymbolShape.Circle, label: '● Cerchio' },
  { value: MapSymbolShape.Square, label: '■ Quadrato' },
  { value: MapSymbolShape.Triangle, label: '▲ Triangolo' },
  { value: MapSymbolShape.Star, label: '★ Stella' },
  { value: MapSymbolShape.Cross, label: '✚ Croce' },
  { value: MapSymbolShape.X, label: '✕ X' },
];

/**
 * Configurazione dell'intersezione per un singolo layer.
 */
export interface IMapLayerIntersection {
  /** Se abilitato, il layer partecipa al calcolo delle intersezioni */
  enabled: boolean;
  /** Titolo mostrato nella tabella risultati */
  title?: string;
  /** Nomi degli attributi da estrarre dalle feature intersecate */
  attributes: string[];
  /** Mappatura da nomi attributo sorgente a nomi campo output */
  aliases?: Record<string, string>;
  /** Area minima (mq) sotto la quale l'intersezione viene ignorata */
  areaTolerance?: number;
  /** Se true, utilizza lo schema legacy MapCadastralAreaItemSchema e il suffisso @cadastral */
  cadastralPreset?: boolean;
  /** Se true, la tabella intersezioni è visibile solo in Back Office */
  backofficeOnly?: boolean;
  /** Numero massimo di risultati da mostrare nella tabella intersezioni */
  maxResults?: number;
}

/**
 * Configurazione dell'interrogazione (Identify) per un singolo layer.
 * Pattern competence/labeling: enabled opzionale con default false,
 * la config resta salvata anche quando l'interrogazione e' disattivata.
 */
export interface IMapLayerIdentify {
  /** Se abilitato, il layer partecipa all'Identify */
  enabled?: boolean;
  /** Titolo mostrato nel pannello risultati */
  title?: string;
  /** Nomi degli attributi da mostrare */
  attributes?: string[];
  /** Mappatura da nomi attributo sorgente a label leggibili */
  aliases?: Record<string, string>;
  /** Se true, l'interrogazione e' visibile solo in Back Office */
  backOfficeOnly?: boolean;
  /** Numero massimo di risultati */
  maxResults?: number;
}

/**
 * Configurazione della competenza per un singolo layer.
 * Un layer con role 'primary' identifica la competenza principale
 * (ente con area di intersezione maggiore).
 * Un layer con role 'secondary' contribuisce alle competenze
 * secondarie, ciascuna associata a un tipo.
 */
export interface IMapLayerCompetence {
  /**
   * Se abilitato, il layer partecipa al riconoscimento
   * della competenza.
   */
  enabled?: boolean;
  /**
   * Ruolo della competenza: 'primary' (max 1 per widget)
   * o 'secondary' (N per widget).
   */
  role?: 'primary' | 'secondary';
  /**
   * Nome della proprieta nelle feature del layer vettoriale
   * che identifica il codice competenza.
   */
  identifyingPropertyName?: string;
  /**
   * ID del tipo di competenza secondaria.
   * Obbligatorio se role === 'secondary'.
   */
  secondaryCompetenceTypeId?: string;
  /**
   * Soglia minima di area (mq) per considerare
   * valida un'intersezione di competenza.
   */
  areaTolerance?: number;
}

export enum MapLayerType {
  /**
   * OpenStreetMap
   */
  OSM = 'osm',
  /**
   * Raster XYZ (Tile)
   */
  XYZ = 'xyz',
  /**
   * Raster WMS
   */
  WMS = 'wms',
  /**
   * ArcGIS REST
   */
  ArcGISRest = 'arcgisRest',
  /**
   * ArcGIS REST (Tile)
   */
  ArcGISRestTile = 'arcgisRestTile',
  /**
   * ArcGIS REST (Vector)
   */
  ArcGISRestVector = 'arcgisRestVector',
  /**
   * WFS - Web Feature Service (Vettoriale, standard OGC)
   */
  WFS = 'wfs',
}

export const MapLayerTypeOptions = [
  { value: MapLayerType.OSM, label: 'OpenStreetMap' },
  { value: MapLayerType.XYZ, label: 'Tile XYZ (Raster)' },
  { value: MapLayerType.WMS, label: 'WMS (Raster)' },
  { value: MapLayerType.ArcGISRest, label: 'ArcGIS REST (Raster)' },
  { value: MapLayerType.ArcGISRestTile, label: 'ArcGIS REST (Tile)' },
  {
    value: MapLayerType.ArcGISRestVector,
    label: 'ArcGIS REST (Vettoriale)',
    allowIntersect: true,
  },
  {
    value: MapLayerType.WFS,
    label: 'WFS (Vettoriale)',
    allowIntersect: true,
  },
];

/** Etichetta leggibile del tipo di layer (es. per i messaggi di verifica) */
export function getMapLayerTypeLabel(layerType: MapLayerType): string {
  return (
    MapLayerTypeOptions.find(option => option.value === layerType)?.label ??
    layerType
  );
}

/** Verifica se il tipo di layer supporta le intersezioni */
export function isIntersectableLayerType(layerType: MapLayerType): boolean {
  return isVectorLayerType(layerType);
}

/** Verifica se il tipo di layer supporta la competenza */
export function isCompetenceCapableLayerType(layerType: MapLayerType): boolean {
  return isVectorLayerType(layerType);
}

/** Verifica se il tipo di layer supporta l'interrogazione (Identify) */
export function isQueryableLayerType(layerType: MapLayerType): boolean {
  return isVectorLayerType(layerType);
}

/**
 * Verifica se un layer di intersezione è visibile nel contesto corrente.
 * I layer con `backofficeOnly` sono nascosti in Frontoffice.
 */
export function isIntersectionLayerVisible(
  layer: IMapLayer,
  isBackoffice: boolean,
): boolean {
  if (layer.intersection?.backofficeOnly && !isBackoffice) return false;
  return true;
}

/**
 * Verifica se un layer interrogabile (Identify) è visibile nel contesto
 * corrente. I layer con `backOfficeOnly` sono nascosti in Frontoffice.
 */
export function isIdentifyLayerVisible(
  layer: IMapLayer,
  isBackoffice: boolean,
): boolean {
  if (layer.identify?.backOfficeOnly && !isBackoffice) return false;
  return true;
}

export function isVectorLayerType(layerType: MapLayerType): boolean {
  return (
    layerType === MapLayerType.ArcGISRestVector ||
    layerType === MapLayerType.WFS
  );
}

export function isMapLayerRenderingSupported(layerType: MapLayerType): boolean {
  return (
    layerType === MapLayerType.ArcGISRestTile ||
    layerType === MapLayerType.XYZ ||
    layerType === MapLayerType.WMS
  );
}
